import { QueryKey, useQueryClient } from '@tanstack/react-query';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import {
  apiHooks,
  REST_API_HOOKS_NAME,
} from '../../../../../hooks/useRestAPIHooks';

import { AsyncTask } from '../../../../../services/AgentsService';
import { createQueryKey } from '@lifeomic/one-query';
import { INFINITE_QUERY_KEY } from '@lifeomic/one-query/cache';
import { useActiveProjectId, useNotify } from '../../../../../hooks';
import { defineMessages, useIntl } from 'react-intl';

type TemplatesContextType = {
  refetchReports: () => void;
  hasMoreReports: boolean | undefined;
  fetchMoreReports: () => void;

  reports: fhir3.ClinicalImpression[];
  deleteReport: (impression: fhir.ClinicalImpression & { id: string }) => void;
  loadingReports: boolean;

  invocations: AsyncTask<{ template_id: string }>[];
  refetchInvocations: () => void;

  generateReport: ({
    templateId,
    instructions,
    onSuccess,
    onError,
  }: {
    templateId: string;
    instructions?: string;
    onSuccess: () => void;
    onError: (err: any) => void;
  }) => void;
};

const TemplatesContext = createContext<TemplatesContextType | undefined>(
  undefined,
);

const messages = defineMessages({
  generateReportSuccess: {
    id: 'TemplatesContext.generateReportSuccess',
    defaultMessage:
      'Report generation started. The reports list will refresh when the report is ready.',
  },
  generateReportFailure: {
    id: 'TemplatesContext.generateReportFailure',
    defaultMessage:
      'Failed to generate report. Please try again or notify support.',
  },
  generateReportNotFound: {
    id: 'TemplatesContext.generateReportNotFound',
    defaultMessage:
      'The template was not found, it may have been deleted or moved. Please use the templates tab to choose a template to use.',
  },
});

export const TemplatesProvider: React.FC<{
  patientId: string;
  children: React.ReactNode;
}> = ({ patientId, children }) => {
  const projectId = useActiveProjectId();
  const queryClient = useQueryClient();

  const [notify] = useNotify();
  const { formatMessage } = useIntl();

  const infiniteReportsQueryPayload = useMemo(() => {
    return {
      patient: patientId,
      _tag: [
        'http://lifeomic.com/fhir/fountain-life-clinical-impression-type|templated-report',
        `http://lifeomic.com/fhir/dataset|${projectId}`,
      ],
      'status:not': 'entered-in-error',
      pageSize: '50', // patient-service wants a string, not a number
    };
  }, [patientId, projectId]);

  const reportsQueryKey: QueryKey = [
    INFINITE_QUERY_KEY,
    createQueryKey(
      REST_API_HOOKS_NAME,
      'POST /v1/fhir/dstu3/ClinicalImpression/_search',

      infiniteReportsQueryPayload,
    ),
  ];

  const {
    data: reports,
    isLoading: loadingReports,
    refetch: refetchReports,
    fetchNextPage: fetchMoreReports,
    isFetchingNextPage: fetchingMoreReports,
    hasNextPage: hasMoreReports,
  } = apiHooks.useInfiniteAPIQuery(
    'POST /v1/fhir/dstu3/ClinicalImpression/_search',
    infiniteReportsQueryPayload,
    {
      // @ts-expect-error - types are not correct
      select: data => {
        return data.pages.flatMap(page =>
          page.entry
            ?.map(entry => entry.resource)
            .filter(resource => resource?.resourceType === 'ClinicalImpression')
            .map(resource => resource as fhir3.ClinicalImpression)
            .sort((a, b) => {
              return (
                new Date(b.meta?.lastUpdated ?? '').getTime() -
                new Date(a.meta?.lastUpdated ?? '').getTime()
              );
            }),
        );
      },
      getNextPageParam: lastPage => {
        const nextLink = lastPage.link?.find(link => link.relation === 'next');
        if (!nextLink) {
          return undefined;
        }
        const params = new URLSearchParams(nextLink?.url);
        return { next: params.get('next') || undefined };
      },
    },
  );

  // this doesn't actually delete it, it PUTs it with status entered-in-error
  const deleteReportMutation = apiHooks.useAPIMutation(
    'PUT /v1/fhir/dstu3/ClinicalImpression/:resourceId',
    {
      onMutate: async ({ id }) => {
        await queryClient.cancelQueries({ queryKey: reportsQueryKey });
        const previousReports = queryClient.getQueryData(
          reportsQueryKey,
        ) as typeof reports;
        if (!previousReports) {
          return;
        }
        queryClient.setQueryData(reportsQueryKey, () => {
          return {
            ...previousReports,
            pages: previousReports.pages.map((page: any) => ({
              ...page,
              entry: page.entry.filter(
                (entry: any) => entry.resource.id !== id,
              ),
            })),
          };
        });
        return { previousReports };
      },
      onError: (_e, _v, context: any) => {
        queryClient.setQueryData(reportsQueryKey, context?.previousImpressions);
      },
    },
  );

  const invocationsQueryKey = useMemo(() => {
    return [
      createQueryKey(
        REST_API_HOOKS_NAME,
        'GET /v1/agents-v2/template-agent/invocations',
        {
          subject_id: patientId,
        },
      ),
    ];
  }, [patientId]);

  const { data: invocations, refetch: refetchInvocations } =
    apiHooks.useAPIQuery<any, AsyncTask<{ template_id: string }>[]>(
      'GET /v1/agents-v2/template-agent/invocations',
      {
        subject_id: patientId,
      },
    );

  // when there are invocations pending, poll invocations for updates, then
  // refetch reports when the invocations are complete
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (!invocations) {
      return;
    }
    if (invocations?.length > 0) {
      interval = setInterval(() => {
        refetchInvocations();
      }, 5000);
    } else {
      refetchReports();
    }
    return () => clearInterval(interval);
  }, [invocations, invocations?.length, refetchInvocations, refetchReports]);

  const generateReportMutation = apiHooks.useAPIMutation(
    'POST /v1/agents-v2/template-agent/invoke',
    {
      onSuccess: () => {
        queryClient.invalidateQueries(invocationsQueryKey);
      },
    },
  );

  const generateReport = useCallback(
    ({
      templateId,
      instructions,
      onSuccess,
      onError,
    }: {
      templateId: string;
      instructions?: string;
      onSuccess: () => void;
      onError: (err: any) => void;
    }) => {
      generateReportMutation.mutate(
        {
          template_id: templateId,
          subject_id: patientId,
          project_id: projectId,
          instructions,
        },
        {
          onSuccess: () => {
            notify(formatMessage(messages.generateReportSuccess), 'success');
            onSuccess?.();
          },
          onError: (err: any) => {
            if (err.response?.status === 404) {
              notify(formatMessage(messages.generateReportNotFound), 'error');
            } else {
              notify(formatMessage(messages.generateReportFailure), 'error');
              onError?.(err);
            }
          },
        },
      );
    },
    [generateReportMutation, patientId, projectId, notify, formatMessage],
  );

  const deleteReport = useCallback(
    (impression: fhir.ClinicalImpression & { id: string }) => {
      deleteReportMutation.mutate({
        ...impression,
        resourceId: impression.id,
        resourceType: 'ClinicalImpression' as const,
        status: 'entered-in-error',
      } as fhir3.ClinicalImpression & { id: string; resourceId: string });
    },
    [deleteReportMutation],
  );

  return (
    <TemplatesContext.Provider
      value={{
        invocations: invocations ?? [],
        // allow children to trigger a refetch of invocations
        refetchInvocations,
        generateReport,

        reports: (reports as unknown as fhir3.ClinicalImpression[]) ?? [],
        // allow children to trigger a refetch of reports
        refetchReports,
        fetchMoreReports,
        hasMoreReports,

        loadingReports: loadingReports || fetchingMoreReports,
        deleteReport,
      }}
    >
      {children}
    </TemplatesContext.Provider>
  );
};

export const useTemplatesContext = (): TemplatesContextType => {
  const context = useContext(TemplatesContext);
  if (!context) {
    throw new Error(
      'useTemplatesContext must be used within a TemplatesProvider',
    );
  }
  return context;
};
